// src/layouts/Main.jsx
import * as React from "react";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Divider,
  useTheme,
  IconButton,
} from "@mui/material";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import Breadcrumbs, { shouldShowBreadcrumbs } from "../components/Breadcrumbs";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import TerminalIcon from "@mui/icons-material/Terminal";
import { ReactComponent as LayoutLeftIcon } from "../assets/icons/layout-left.svg";

// Import SVGs as React components
import { ReactComponent as DashboardIcon } from "../assets/icons/cube.svg";
import { ReactComponent as DatabaseIcon } from "../assets/icons/database.svg";
import { ReactComponent as SettingsIcon } from "../assets/icons/setting.svg";
import { ReactComponent as BillingIcon } from "../assets/icons/credit-card.svg";
import { ReactComponent as LogoutIcon } from "../assets/icons/logout.svg";
import OrgSwitcher from "../components/OrgSwitcher";
import ImpersonationBanner from "../components/ImpersonationBanner";

export default function Main({ variant = "default", title, subtitle, children }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuth = useIsAuthenticated();
  const { instance } = useMsal();
  const account = instance.getActiveAccount();
  // For federated users `account.username` is the synthetic Entra UPN
  // (`<oid>@<tenant>.onmicrosoft.com`), so its first char is a GUID digit.
  // Prefer the display name claim, falling back to the username only if no
  // name exists and the username is a real-looking string (not a UPN GUID).
  const _displayName = (account?.name || "").trim();
  const _isUpnGuid = /@[^@]+\.onmicrosoft\.com$/i.test(account?.username || "");
  const userInitial =
    (_displayName && _displayName[0]?.toUpperCase()) ||
    (!_isUpnGuid && account?.username?.[0]?.toUpperCase()) ||
    "?";
    
  // Through /logout, which also clears the admin hint and any view-as scope before signing out;
  // calling logoutRedirect here directly left both behind for the next person on this browser.
  const handleLogout = () => navigate("/logout");

  const [collapsed, setCollapsed] = React.useState(() => {
    try {
      const saved = localStorage.getItem('sidebarCollapsed');
      return saved === 'true';
    } catch {
      return false;
    }
  });
  const sidebarWidth = collapsed ? 72 : 280;

  React.useEffect(() => {
    try {
      localStorage.setItem('sidebarCollapsed', String(collapsed));
    } catch {
      // ignore storage errors
    }
  }, [collapsed]);

  // ---------- DYNAMIC PAGE TITLE ----------
  React.useEffect(() => {
    const path = location.pathname;
    let pageTitle = "Assertion AI Studio";

    if (path === "/" || path.startsWith("/dashboard") || path.startsWith("/projects"))
      pageTitle = "Projects – Assertion AI Studio";
    else if (path.startsWith("/dictionaries/import-snowflake"))
      pageTitle = "Import from Snowflake – Assertion AI Studio";
    else if (path.startsWith("/dictionaries/new"))
      pageTitle = "New Data Dictionary – Assertion AI Studio";
    else if (path.startsWith("/dictionaries/"))
      pageTitle = "Data Dictionary – Assertion AI Studio";
    else if (path.startsWith("/dictionaries"))
      pageTitle = "Dictionaries – Assertion AI Studio";
    else if (path.startsWith("/billing"))
      pageTitle = "Billing – Assertion AI Studio";
    else if (path.startsWith("/settings"))
      pageTitle = "Settings – Assertion AI Studio";
    else if (path.includes("/report") && path.startsWith("/analyze"))
      pageTitle = "Analysis Report – Assertion AI Studio";
    else if (path.startsWith("/analyze"))
      pageTitle = "Explore – Assertion AI Studio";
    else if (path.endsWith("/notebook") && path.startsWith("/runs"))
      pageTitle = "Decisions – Assertion AI Studio";

    document.title = pageTitle;
  }, [location.pathname]);

  // ---------- AUTH LAYOUT ----------
  if (variant === "auth") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          backgroundImage: "url(/shield.svg)",
          backgroundRepeat: "no-repeat",
          backgroundPosition: { xs: "calc(100% + 150px) 50%", sm: "calc(100% + 200px) 50%" },
          backgroundSize: { xs: "auto 50%", sm: "auto 70%" },
        }}
      >
        <Box sx={{ p: 4, textAlign: "center" }}>
          <img src="/header_logo.png" alt="Assertion AI" width={48} />
          <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
            ASSERTION AI
          </Typography>
        </Box>

        <Box sx={{ flex: 1, display: "grid", placeItems: "center", px: 2, py: 6 }}>
          <Box sx={{ maxWidth: 700, textAlign: "center" }}>
            {title && (
              <Typography
                variant="h3"
                sx={{ mb: 2, fontWeight: 700, fontFamily: theme.typography.serif.fontFamily }}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="subtitle1" sx={{ mb: 4, color: "text.secondary" }}>
                {subtitle}
              </Typography>
            )}
            {children}
          </Box>
        </Box>
      </Box>
    );
  }

  // ---------- MAIN APP SHELL ----------
  const mainNavItems = [
    { to: "/", label: "Projects", icon: <DashboardIcon width={24} height={24} /> },
    { to: "/data", label: "Data", icon: <DatabaseIcon width={24} height={24} /> },
    { to: "/analyze", label: "Explore", icon: <AutoGraphIcon width={24} height={24} />, filledIcon: true },
  ];

  const accountNavItems = [
    { to: "/billing", label: "Billing", icon: <BillingIcon width={24} height={24} /> },
    { to: "/settings", label: "Settings", icon: <SettingsIcon width={24} height={24} /> },
    // The product switcher, analytics side: the same account's Memory product (its own shell at
    // /memory, /plugin, /agent — see components/MemoryNav.jsx, whose "Analytics" link mirrors this).
    { to: "/memory", label: "Memory", icon: <TerminalIcon width={24} height={24} />, filledIcon: true },
  ];

  const navItemSx = (theme) => ({
    borderRadius: 1,
    mb: 1,
    "&.active": {
      bgcolor: theme.palette.primary.light,
      color: theme.palette.primary.main,
      "& svg": {
        color: theme.palette.primary.main,
        fill: "none",
        stroke: `${theme.palette.primary.main} !important`,
        "& path": {
          stroke: `${theme.palette.primary.main} !important`,
        },
      },
      "& .MuiTypography-root": {
        color: theme.palette.primary.main,
      },
    },
  });

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: sidebarWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: sidebarWidth,
            transition: 'width 0.3s cubic-bezier(.4,0,.2,1)',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            p: 2,
            overflowX: 'hidden',
          },
        }}
      >
        {/* -------- Top Section -------- */}
        <Box>
          {/* Top row: logo and toggle icon (align right) */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, position: 'relative', height: 40 }}>
            {/* Logo/icon clickable */}
            <Box
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: collapsed ? 40 : 'auto',
                height: 40,
              }}
              onClick={() => navigate('/')}
            >
              {collapsed ? (
                <img src="/header_logo.png" alt="Assertion AI" width={32} height={32} />
              ) : (
                <img src="/header_logo.png" alt="Assertion AI" height={36} />
              )}
            </Box>
            {!collapsed && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                {isAuth && (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                    onClick={() => navigate('/settings')}
                  >
                    <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                      {userInitial}
                    </Avatar>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ maxWidth: 125, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {account?.name || account?.username}
                    </Typography>
                  </Box>
                )}
              </>
            )}
            {/* Chevron toggle (hide when collapsed! only show when expanded) */}
            {!collapsed && (
              <IconButton
                edge="end"
                aria-label="collapse sidebar"
                onClick={() => setCollapsed(true)}
                size="small"
                sx={{ ml: 1 }}
              >
                <ChevronLeftIcon />
              </IconButton>
            )}
          </Box>

          {/* Navigation - Main */}
          <List>
            {mainNavItems.map((item) => (
              <ListItemButton
                key={item.to}
                component={NavLink}
                to={item.to}
                sx={{
                  ...navItemSx(theme),
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  px: collapsed ? 0.75 : 2,
                  ...(item.filledIcon && {
                    "&.active svg": {
                      fill: `${theme.palette.primary.main} !important`,
                      stroke: "none !important",
                      color: theme.palette.primary.main,
                    },
                  }),
                }}
              >
                <ListItemIcon sx={{ minWidth: 0, mr: collapsed ? 0 : 1, justifyContent: 'center' }}>{item.icon}</ListItemIcon>
                {!collapsed && <ListItemText primary={item.label} />}
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* -------- Bottom Section -------- */}
        {isAuth && (
          <Box>
            <OrgSwitcher collapsed={collapsed} />
            <List>
              {accountNavItems.map((item) => (
                <ListItemButton
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  sx={{ ...navItemSx(theme), justifyContent: collapsed ? 'center' : 'flex-start', px: collapsed ? 0.75 : 2 }}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: collapsed ? 0 : 1, justifyContent: 'center' }}>{item.icon}</ListItemIcon>
                  {!collapsed && <ListItemText primary={item.label} />}
                </ListItemButton>
              ))}
            </List>
            <Divider sx={{ my: 2 }} />
            <List>
              <ListItemButton onClick={handleLogout} sx={{ justifyContent: collapsed ? 'center' : 'flex-start', px: collapsed ? 0.75 : 2 }}>
                <ListItemIcon sx={{ minWidth: 0, mr: collapsed ? 0 : 1, justifyContent: 'center' }}>
                  <LogoutIcon width={24} height={24} />
                </ListItemIcon>
                {!collapsed && <ListItemText primary="Logout" />}
              </ListItemButton>
            </List>
          </Box>
        )}
      </Drawer>

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 2,
          width: { sm: `calc(100% - ${sidebarWidth}px)` },
          transition: 'width 0.3s cubic-bezier(.4,0,.2,1)',
          ml: 0,
        }}
      >
        <ImpersonationBanner />
        {/* Top row: hamburger (when sidebar collapsed) + breadcrumbs.
            On depth-1 routes (Projects/Data/Explore/Settings/Billing) the
            breadcrumb would just duplicate the page title, so we hide the
            whole strip — unless the sidebar is collapsed, in which case we
            still need the toggle button visible. */}
        {(collapsed || shouldShowBreadcrumbs(location.pathname)) && (
          <Box sx={{
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            minHeight: 40,
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            backgroundColor: theme.palette.background.default,
            py: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}>
            {collapsed && (
              <IconButton
                onClick={() => setCollapsed(false)}
                size="small"
                sx={{
                  mr: 2,
                  color: 'text.secondary',
                  '& svg': { width: 20, height: 20 },
                  '& svg *': {
                    stroke: 'currentColor',
                  },
                }}
                aria-label="Open menu"
              >
                <LayoutLeftIcon />
              </IconButton>
            )}
            <Breadcrumbs />
          </Box>
        )}
        {title && (
          <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {subtitle}
          </Typography>
        )}
        <Outlet />
        {children}
      </Box>
    </Box>
  );
}